import React, { useState } from "react";
import { FolderPlus } from "lucide-react";
import { useForm } from "react-hook-form";
import { clsx } from "clsx";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToastDefault } from "@/hooks/use-toast-default";
import { useNavigate } from "react-router";
import { useCreateFolderMutation } from "@/api/files";

const specialCharacters = /[!@#$%^&*()+=[\]{};':"\\|,.<>/?]+/;

interface CreateFolderModalProps {
  currentFolderId: string;
  isReadOnly?: boolean;
}

interface FormInputs {
  folderName: string;
}

const CreateFolderModal: React.FC<CreateFolderModalProps> = ({ currentFolderId, isReadOnly }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToastDefault();

  const [createFolder] = useCreateFolderMutation();

  const methods = useForm<FormInputs>();

  const onSubmit = async (payload: FormInputs) => {
    const result = await createFolder({ name: payload.folderName, parent_id: currentFolderId }).unwrap();

    setIsOpen(false);

    showToast("Folder Created", `Folder "${payload.folderName}" has been created successfully.`);
    navigate(`/drive/${result.id}`);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="secondary"
          disabled={isReadOnly}
          borderStyle="border-gray-300 border-solid"
          onClick={() => setIsOpen(true)}
        >
          <FolderPlus className="mr-2 h-5 w-5" />
          Create new folder
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="folderName" className="text-right">
                Name
              </Label>
              <div className="col-span-3">
                <Input
                  {...methods.register("folderName", {
                    required: "This is required.",
                    validate: value => !specialCharacters.test(value) || "Folder name contains special characters.",
                  })}
                  className={clsx({
                    "border-red-500": methods.formState.errors.folderName,
                  })}
                />
                {!!methods.formState.errors.folderName && (
                  <p className="mt-2 text-sm text-red-500">{methods.formState.errors.folderName.message}</p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={methods.formState.isSubmitting}>
              {methods.formState.isSubmitting ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateFolderModal;
